import { supabase } from '@/integrations/supabase/client';
import { withServiceErrorHandling } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';

export interface LeaderboardEntry {
  user_id: string;
  full_name: string;
  role: string;
  avatar_url?: string;
  
  // Activity metrics
  posts_count: number;
  engagement_rate: number;
  
  // Pipeline metrics
  deals_influenced: number;
  pipeline_value: number;
  mrr_generated: number;
  
  // Social metrics
  profile_views: number;
  connection_requests: number;
  
  // Computed
  total_score: number;
  rank: number;
  trend: "up" | "down" | "stable";
}

export class LeaderboardService {
  private static readonly logger = logger.child({ service: 'LeaderboardService' });

  /**
   * Get sales leaderboard for organization
   */
  static async getSalesLeaderboard(
    organizationId: string,
    timeRange: "7d" | "30d" | "90d" | "ytd" = "30d"
  ): Promise<LeaderboardEntry[]> {
    return withServiceErrorHandling(async () => {
      this.logger.info('Fetching sales leaderboard', { organizationId, timeRange });

      const daysAgo = timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : timeRange === "90d" ? 90 : 365;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - daysAgo);

      // Get all users in organization with sales roles
      const { data: userRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select(`
          user_id,
          role,
          profiles:user_id (
            full_name,
            avatar_url
          )
        `)
        .eq('organization_id', organizationId)
        .in('role', ['owner', 'admin', 'editor', 'viewer']);

      if (rolesError) throw rolesError;

      // Get posts stats for each user
      const leaderboard: LeaderboardEntry[] = await Promise.all(
        (userRoles || []).map(async (userRole) => {
          const userId = userRole.user_id;
          const profile = Array.isArray(userRole.profiles) ? userRole.profiles[0] : userRole.profiles;

          // Get posts count
          const { data: posts } = await supabase
            .from('posts')
            .select('id')
            .eq('organization_id', organizationId)
            .eq('created_by', userId)
            .eq('status', 'published')
            .gte('published_at', startDate.toISOString());

          const postsCount = posts?.length || 0;
          
          // Get engagement from analytics table
          const { data: analytics } = await supabase
            .from('post_analytics')
            .select('engagement_rate')
            .in('post_id', posts?.map(p => p.id) || []);

          const avgEngagement = analytics?.length
            ? analytics.reduce((sum, a) => sum + (Number(a.engagement_rate) || 0), 0) / analytics.length
            : 0;

          // Get attribution events (deals influenced)
          const { data: attributionEvents } = await supabase
            .from('attribution_events')
            .select('event_type, deal_amount, mrr_value, post_id')
            .eq('organization_id', organizationId)
            .in('post_id', posts?.map(p => p.id) || [])
            .in('event_type', ['trial_signup', 'conversion', 'deal_created']);

          const dealsInfluenced = attributionEvents?.filter(e => e.event_type === 'deal_created').length || 0;
          const pipelineValue = attributionEvents
            ?.filter(e => e.event_type === 'deal_created')
            .reduce((sum, e) => sum + (e.deal_amount || 0), 0) || 0;
          const mrrGenerated = attributionEvents
            ?.filter(e => e.event_type === 'conversion')
            .reduce((sum, e) => sum + (e.mrr_value || 0), 0) || 0;

          // Calculate total score (weighted formula)
          const totalScore = Math.min(
            100,
            Math.round(
              (postsCount * 0.5) +
              (avgEngagement * 5) +
              (dealsInfluenced * 10) +
              (mrrGenerated / 100)
            )
          );

          return {
            user_id: userId,
            full_name: profile?.full_name || 'Unknown User',
            role: userRole.role,
            avatar_url: profile?.avatar_url,
            posts_count: postsCount,
            engagement_rate: avgEngagement,
            deals_influenced: dealsInfluenced,
            pipeline_value: pipelineValue,
            mrr_generated: mrrGenerated,
            profile_views: Math.floor(Math.random() * 3000), // Mock - would come from LinkedIn API
            connection_requests: Math.floor(Math.random() * 200), // Mock - would come from platform APIs
            total_score: totalScore,
            rank: 0, // Will be assigned after sorting
            trend: "stable", // Would calculate from historical data
          };
        })
      );

      // Sort by total score and assign ranks
      leaderboard.sort((a, b) => b.total_score - a.total_score);
      leaderboard.forEach((entry, index) => {
        entry.rank = index + 1;
      });

      this.logger.info('Sales leaderboard fetched', { 
        organizationId, 
        count: leaderboard.length 
      });

      return leaderboard;
    }, { operation: 'getSalesLeaderboard', table: 'user_roles' });
  }

  /**
   * Get individual user performance compared to team
   */
  static async getUserPerformance(
    userId: string,
    organizationId: string
  ): Promise<{
    userEntry: LeaderboardEntry;
    teamAverage: {
      posts_count: number;
      engagement_rate: number;
      deals_influenced: number;
      total_score: number;
    };
  }> {
    return withServiceErrorHandling(async () => {
      const leaderboard = await this.getSalesLeaderboard(organizationId);
      const userEntry = leaderboard.find(e => e.user_id === userId);

      if (!userEntry) {
        throw new Error('User not found in leaderboard');
      }

      const teamAverage = {
        posts_count: Math.round(
          leaderboard.reduce((sum, e) => sum + e.posts_count, 0) / leaderboard.length
        ),
        engagement_rate: leaderboard.reduce((sum, e) => sum + e.engagement_rate, 0) / leaderboard.length,
        deals_influenced: Math.round(
          leaderboard.reduce((sum, e) => sum + e.deals_influenced, 0) / leaderboard.length
        ),
        total_score: Math.round(
          leaderboard.reduce((sum, e) => sum + e.total_score, 0) / leaderboard.length
        ),
      };

      return { userEntry, teamAverage };
    }, { operation: 'getUserPerformance', table: 'user_roles' });
  }
}
